import { type ReactNode, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Upload,
} from "lucide-react";
import { useImmer } from "use-immer";

import {
  IgAiIcon,
  IgAlert,
  IgChip,
  IgDot,
  IgIconButton,
  IgMonoText,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { Collapse, Divider, Stack, useTheme } from "@mui/material";

import type { WorkspaceDraft, WorkspaceVersion } from "../../types";

/** Zone shown when the "Version history" segment is selected. */
export interface VersionHistoryPanelProps {
  activeLabel?: string;
  versions: WorkspaceVersion[];
  draft: WorkspaceDraft | null;
  draftContent?: ReactNode;
  /** Byline name for chat-built versions (e.g. "Atlas"). */
  aiAuthorName: string;
  /** Rollback re-publish of an older version (opens the confirm modal). */
  onPublishVersion: (version: number) => void;
  onDeleteVersion: (version: number) => void;
  /**
   * Renames a version, or clears the name back to its default with `null`.
   *
   * Optional because the capability is per-host: a domain whose API has no
   * rename simply does not pass it, and the card draws its title as the static
   * text it always did. Threaded as a callback rather than as a mutation hook
   * so this view stays presentational.
   */
  onRenameVersion?: (
    version: number,
    title: string | null
  ) => void | Promise<void>;
  /** A rename is in flight; the open input holds until it lands. */
  isRenamingVersion?: boolean;
  /**
   * Renders a version's read-only code inside its expanded card. Omitted when
   * the domain has no per-version detail read; cards then have no chevron.
   */
  renderVersionCode?: (version: number) => ReactNode;
  /** Domain-specific ceiling on retained versions, or `null` for no limit. */
  versionLimit: { limit: number; warnAt: number } | null;
  readOnly: boolean;
}

const formatVersionTimestamp = (ts: number): string =>
  DateTime.fromMillis(ts).toLocaleString({
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/**
 * The status chip on a version card. The active version reads "Live" with a
 * dot — the same presentation as the workspace title bar's chip — so the card
 * and the header never disagree about what live looks like.
 */
const VersionStatusChip = ({
  status,
  activeLabel = "Live",
}: {
  activeLabel?: string;
  status: WorkspaceVersion["status"] | "draft";
}) => {
  const theme = useTheme();
  if (status === "active") {
    return (
      <IgChip
        size="small"
        color="success"
        label={activeLabel}
        sx={{ px: 1 }}
        icon={<IgDot color={theme.palette.success.main} size={6} />}
      />
    );
  }
  if (status === "failed") {
    return <IgChip size="small" color="error" label="Failed" sx={{ px: 1 }} />;
  }
  if (status === "draft") {
    return <IgChip size="small" color="warning" label="Draft" sx={{ px: 1 }} />;
  }
  // Superseded — "previously active", per the version DTO — gets NO chip.
  //
  // The status VALUE is load-bearing and stays: it is a pointer state across
  // the version DTO union and `activateVersionPointer`. The LABEL was not. A
  // chip is for a fact a reader would otherwise miss, and "this is an old
  // version" is already said by the row's position in a version-ordered list —
  // one that chipped every row but the newest, so the chip read as a warning
  // about the normal case. What is left carries real meaning: `Draft`, `Live`,
  // `Failed`.
  return null;
};

/** The card's title line: explicit title, else notes, else "Version {n}". */
const versionTitle = (version: WorkspaceVersion): string =>
  version.title?.trim() ||
  version.notes?.trim() ||
  `Version ${version.version}`;

const MAX_VERSION_TITLE_LENGTH = 255;

/**
 * The card's title, renamed where it is read.
 *
 * The same shape as `WorkspaceTitle`: clicking the text (or the pencil that
 * appears on hover) turns it into an input, Enter or blur commits, Escape
 * reverts. It differs in one place — an EMPTY value is a real answer here. A
 * version cannot be left nameless the way an entity cannot, because `v{n}`
 * beside it never goes away, so clearing the field is how a merchant undoes a
 * rename and gets the default label back.
 *
 * Without `onRename` this is the static text the card always drew.
 */
const VersionTitle = ({
  version,
  onRename,
  saving = false,
}: {
  version: WorkspaceVersion;
  onRename?: (version: number, title: string | null) => void | Promise<void>;
  saving?: boolean;
}) => {
  const theme = useTheme();
  const current = versionTitle(version);
  const [editing, setEditing] = useImmer(false);
  const [draft, setDraft] = useImmer(version.title ?? "");
  // Exactly one commit per edit session, armed by OPENING the session.
  //
  // It cannot be armed by Escape and cleared by the blur Escape "triggers":
  // closing the editor removes a focused input from the document, and a browser
  // dispatches no blur for a node that leaves the document (React synthesizes
  // none either). A guard armed that way is never cleared, so it swallows the
  // NEXT rename instead — leaving the field open on text nobody saved. Arming
  // on open inverts that: whichever of Enter, blur or Escape ends the session
  // disarms it, and the next open re-arms it, so no handler can commit against
  // a session that has already ended.
  const isEditSessionOpenRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(version.title ?? "");
  }, [version.title, editing, setDraft]);

  if (onRename === undefined) {
    return (
      <IgTypography variant="body2" fontWeight={600} noWrap>
        {current}
      </IgTypography>
    );
  }

  const beginEditing = () => {
    isEditSessionOpenRef.current = true;
    setEditing(true);
  };

  /** Ends the session for good — the one place the guard is disarmed. */
  const endEditing = () => {
    isEditSessionOpenRef.current = false;
    setEditing(false);
  };

  const commit = async () => {
    if (!isEditSessionOpenRef.current) return;
    const trimmed = draft.trim();
    endEditing();
    if (trimmed === (version.title ?? "").trim()) return;
    await onRename(version.version, trimmed === "" ? null : trimmed);
  };

  /** Escape: the typed value is thrown away and nothing is sent. */
  const revert = () => {
    setDraft(version.title ?? "");
    endEditing();
  };

  if (editing) {
    return (
      <IgTextField
        autoFocus
        fullWidth
        size="small"
        label={`Version ${version.version} name`}
        disabled={saving}
        slotProps={{ htmlInput: { maxLength: MAX_VERSION_TITLE_LENGTH } }}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void commit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            revert();
          }
        }}
        sx={{ maxWidth: 360 }}
      />
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        minWidth: 0,
        // Laid out at all times and merely transparent, so revealing it on
        // hover never reflows the title beside it.
        "&:hover .VersionTitle-pencil": { opacity: 1 },
      }}
    >
      <IgTypography
        variant="body2"
        fontWeight={600}
        noWrap
        onClick={beginEditing}
        sx={{
          minWidth: 0,
          cursor: "text",
          borderRadius: "6px",
          px: 0.5,
          mx: -0.5,
          "&:hover": {
            bgcolor: theme.igBackgroundColors.interactive.neutral.hover,
          },
        }}
      >
        {current}
      </IgTypography>
      <IgIconButton
        className="VersionTitle-pencil"
        aria-label={`Rename version ${version.version}`}
        size="small"
        onClick={beginEditing}
        sx={{ opacity: 0, transition: "opacity 120ms ease" }}
      >
        <Pencil size={13} />
      </IgIconButton>
    </Stack>
  );
};

/**
 * The author + timestamp byline. Chat-built versions are attributed to the
 * domain's AI assistant with the AI sparkle; everything else to the publishing
 * user.
 *
 * An unresolved publisher reads "Unknown", NEVER the raw `publishedByUserId`.
 * A user id is an identifier, not a label — falling back to it printed a bare
 * UUID in the byline of every manually-published version, which reads as a bug
 * to a merchant and conveys nothing even when it is correct. The id stays on
 * the version for correlation; it is never display text.
 */
const VersionByline = ({
  version,
  aiAuthorName,
}: {
  version: WorkspaceVersion;
  aiAuthorName: string;
}) => {
  const isAiAuthored = version.origin === "chat";
  const authorName = isAiAuthored
    ? aiAuthorName
    : (version.publishedByUser?.name ?? "Unknown");

  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      {isAiAuthored ? (
        <IgAiIcon surface="none" size={14} aria-label="AI-built version" />
      ) : null}
      <IgTypography variant="caption" color="text.secondary">
        {authorName}
        {version.publishedAtTs !== null
          ? ` · ${formatVersionTimestamp(version.publishedAtTs)}`
          : ""}
      </IgTypography>
    </Stack>
  );
};

const VersionCard = ({
  version,
  activeLabel,
  aiAuthorName,
  onPublishVersion,
  onDeleteVersion,
  onRenameVersion,
  isRenamingVersion,
  renderVersionCode,
  readOnly,
}: {
  activeLabel?: string;
  version: WorkspaceVersion;
  aiAuthorName: string;
  onPublishVersion: (version: number) => void;
  onDeleteVersion: (version: number) => void;
  onRenameVersion?: (
    version: number,
    title: string | null
  ) => void | Promise<void>;
  isRenamingVersion?: boolean;
  renderVersionCode?: (version: number) => ReactNode;
  readOnly: boolean;
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useImmer(false);

  const isActive = version.status === "active";
  const canExpand = renderVersionCode !== undefined;
  const canRepublish = !readOnly && !isActive;
  const canDelete = !readOnly && !isActive;
  // The live version is renameable, unlike republish and delete: a name is
  // about finding the version again, not about what serves.
  const canRename = !readOnly && onRenameVersion !== undefined;

  return (
    <Stack
      sx={{
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: 1.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        onClick={canExpand ? () => setIsExpanded((open) => !open) : undefined}
        role={canExpand ? "button" : undefined}
        aria-expanded={canExpand ? isExpanded : undefined}
        aria-label={canExpand ? `Toggle version ${version.version}` : undefined}
        sx={{ px: 2, py: 1.5, cursor: canExpand ? "pointer" : "default" }}
      >
        <IgMonoText
          value={`v${version.version}`}
          variant="body2"
          fontWeight={700}
        />
        <Stack
          spacing={0.25}
          sx={{ flex: 1, minWidth: 0 }}
          // The row toggles the card; the title inside it does not. Without
          // this, reaching for the pencil collapses the card under the cursor.
          onClick={(event) => {
            if (canRename) event.stopPropagation();
          }}
        >
          <VersionTitle
            version={version}
            onRename={canRename ? onRenameVersion : undefined}
            saving={isRenamingVersion}
          />
          <VersionByline version={version} aiAuthorName={aiAuthorName} />
        </Stack>

        <VersionStatusChip activeLabel={activeLabel} status={version.status} />

        {/* Row actions stop click propagation so they never toggle the card. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={(event) => event.stopPropagation()}
        >
          {canRepublish ? (
            <IgTooltip title={`Publish v${version.version}`}>
              <span>
                <IgIconButton
                  size="small"
                  onClick={() => onPublishVersion(version.version)}
                  aria-label={`Publish version ${version.version}`}
                >
                  <Upload size={16} />
                </IgIconButton>
              </span>
            </IgTooltip>
          ) : null}
          {canDelete ? (
            <IgTooltip title={`Delete v${version.version}`}>
              <span>
                <IgIconButton
                  size="small"
                  color="error"
                  onClick={() => onDeleteVersion(version.version)}
                  aria-label={`Delete version ${version.version}`}
                >
                  <Trash2 size={16} />
                </IgIconButton>
              </span>
            </IgTooltip>
          ) : null}
          {canExpand ? (
            <IgIconButton
              size="small"
              onClick={() => setIsExpanded((open) => !open)}
              aria-label={
                isExpanded
                  ? `Collapse version ${version.version}`
                  : `Expand version ${version.version}`
              }
            >
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </IgIconButton>
          ) : null}
        </Stack>
      </Stack>

      {canExpand ? (
        <Collapse in={isExpanded} mountOnEnter unmountOnExit>
          <Divider />
          <Stack sx={{ p: 2 }}>{renderVersionCode?.(version.version)}</Stack>
        </Collapse>
      ) : null}
    </Stack>
  );
};

/** The unpublished draft, shown above the published versions. */
const DraftCard = ({
  draft,
  content,
}: {
  draft: WorkspaceDraft;
  content?: ReactNode;
}) => {
  const theme = useTheme();
  const authorName = draft.updatedByUser?.name ?? null;
  const [expanded, setExpanded] = useImmer(false);

  return (
    <Stack>
      <Stack
        role={content ? "button" : undefined}
        tabIndex={content ? 0 : undefined}
        aria-label={content ? "Toggle saved draft" : undefined}
        aria-expanded={content ? expanded : undefined}
        onClick={content ? () => setExpanded(!expanded) : undefined}
        onKeyDown={
          content
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setExpanded(!expanded);
                }
              }
            : undefined
        }
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          border: `1px solid ${theme.igBorders.core.default}`,
          borderRadius: 1.5,
          px: 2,
          py: 1.5,
        }}
      >
        <IgMonoText value="—" variant="body2" fontWeight={700} />
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <IgTypography variant="body2" fontWeight={600}>
            {content ? "Saved draft" : "Draft"}
          </IgTypography>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {draft.origin === "chat" ? (
              <IgAiIcon surface="none" size={14} aria-label="AI-built draft" />
            ) : null}
            <IgTypography variant="caption" color="text.secondary">
              {authorName}
              {draft.lastUpdateTs != null
                ? `${authorName ? " · " : ""}${formatVersionTimestamp(
                    draft.lastUpdateTs
                  )}`
                : ""}
            </IgTypography>
          </Stack>
        </Stack>
        <VersionStatusChip status="draft" />
        {content ? (
          expanded ? (
            <ChevronDown size={16} aria-hidden />
          ) : (
            <ChevronRight size={16} aria-hidden />
          )
        ) : null}
      </Stack>
      {content ? (
        <Collapse in={expanded} mountOnEnter unmountOnExit>
          <Stack sx={{ p: 2 }}>{content}</Stack>
        </Collapse>
      ) : null}
    </Stack>
  );
};

/**
 * The version-history view: one card per published version, newest first, with
 * the in-progress draft on top. A card expands inline to that version's own
 * detail (via {@link VersionHistoryPanelProps.renderVersionCode} — a rule
 * renders its selection-logic graph there), so inspecting an old version never
 * leaves the page.
 */
export const VersionHistoryPanel = ({
  versions,
  activeLabel,
  draft,
  draftContent,
  aiAuthorName,
  onPublishVersion,
  onDeleteVersion,
  onRenameVersion,
  isRenamingVersion,
  renderVersionCode,
  versionLimit,
  readOnly,
}: VersionHistoryPanelProps) => {
  const orderedVersions = [...versions].sort((a, b) => b.version - a.version);

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0, overflowY: "auto" }}>
      {versionLimit !== null && versions.length > versionLimit.warnAt ? (
        <IgAlert
          alertKey="workspace-version-limit-warning"
          severity="warning"
          title="Version limit"
          message={`This workspace is limited to ${versionLimit.limit} versions. Delete older versions before publishing more.`}
        />
      ) : null}

      {draft ? <DraftCard draft={draft} content={draftContent} /> : null}

      {orderedVersions.map((version) => (
        <VersionCard
          key={version.id}
          version={version}
          activeLabel={activeLabel}
          aiAuthorName={aiAuthorName}
          onPublishVersion={onPublishVersion}
          onDeleteVersion={onDeleteVersion}
          onRenameVersion={onRenameVersion}
          isRenamingVersion={isRenamingVersion}
          renderVersionCode={renderVersionCode}
          readOnly={readOnly}
        />
      ))}

      {!draft && orderedVersions.length === 0 ? (
        <IgTypography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No published versions yet. Publish the draft to create version 1.
        </IgTypography>
      ) : null}
    </Stack>
  );
};
